import { motion, useScroll, useTransform } from 'framer-motion';
import { classes } from 'html-classes';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import type React from 'react';
import { useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { company } from '~/company/Company';
import { useSafeAreaInsets } from '~/hooks/useSafeAreaInsets';
import { cartStore } from '~/stores';
import Icon from '../../../components/Icon/Icon';

import { useOffsetTop } from './hooks';
import styles from './MobileHeaderLayout.module.scss';

interface MobileHeaderLayoutProps {
  content?: React.ReactElement;
  showSearch?: boolean;
  className?: string;
  showPersonalData?: boolean;
  isSticky?: boolean;
  toolbarContentClassName?: string;
  toolbarClassName?: string;
}

const SEARCH_PAGE_LIST = ['/shop', '/explore', '/c/'];
const MAIN = '/';

const MobileHeaderLayoutLocalee = ({
  content,
  showSearch,
  className,
  showPersonalData,
  isSticky,
  toolbarContentClassName,
  toolbarClassName,
}: MobileHeaderLayoutProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const topBlockRef = useRef<HTMLDivElement>(null);

  const { offset, offsetRef } = useOffsetTop();
  const { top: safeAreaTop } = useSafeAreaInsets();

  const handleNavigatePersonalData = () => {
    navigate('/cabinet/personal-data');
  };

  const { scrollY } = useScroll();

  const stickyPosition = useTransform(scrollY, () =>
    offset <= 0 ? 'fixed' : 'static',
  );
  const stickyBg = useTransform(scrollY, () =>
    offset <= 0 ? 'white' : 'transparent',
  );
  const stickyTop = useTransform(scrollY, () => (offset <= 0 ? '0' : 'none'));

  const paddingTopInput = useTransform(
    scrollY,
    [
      0,
      Number(topBlockRef.current?.clientHeight),
      Number(containerRef.current?.clientHeight),
    ],
    [16, 16, safeAreaTop],
  );

  return (
    <div
      className={classes([
        styles.container,
        isSticky && styles.sticky,
        className,
      ])}
      ref={containerRef}
      data-company={company.name}
    >
      {content && (
        <div
          ref={topBlockRef}
          className={classes([
            styles.toolbar,
            'content-layout',
            toolbarClassName,
          ])}
        >
          <div
            className={classes([
              styles.toolbarContent,
              toolbarContentClassName,
            ])}
          >
            {content}
          </div>
          {showPersonalData && (
            <div
              data-button="header-mobile-personal-data"
              className={classes([
                styles.cart,
                cartStore.totalCartCount && styles.cartFull,
              ])}
              onClick={handleNavigatePersonalData}
            >
              <Icon type="filter" size={24} />
            </div>
          )}
        </div>
      )}
      {(pathname === MAIN ||
        SEARCH_PAGE_LIST.some((i) => pathname.includes(i)) ||
        showSearch) && (
        <div ref={offsetRef} className={styles.mainInfoContainerContainer}>
          <motion.div
            className={styles.mainInfoContainer}
            style={{
              background: stickyBg,
              position: stickyPosition,
              top: stickyTop,
              paddingTop: paddingTopInput,
            }}
          >
            <div
              className={classes([
                'content-layout header-search-container',
                styles.searchBar,
              ])}
            >
              <Link
                data-link="header-mobile-search"
                className={'header-search'}
                to="/search"
                replace={true}
                data-company={company.name}
              >
                <Icon className="header-search__icon" type="search" size={24} />

                <div className="header-search__text">
                  {t('phrases:searchFor')}
                </div>
              </Link>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default observer(MobileHeaderLayoutLocalee);
